import { useState, useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Settings2, Link } from 'lucide-react'
import { jobsApi, getApiErrorMessage } from '@/services/api'
import type { JobCreateRequest } from '@/types'
import { buildCheckoUrl } from '@/types'
import JobFilters, { defaultCheckoFilters } from '@/components/JobFilters'
import type { CheckoFilterParams } from '@/types'

function generateJobName(filters: CheckoFilterParams): string {
  const parts: string[] = []
  if (filters.entity === 'organizations') parts.push('Организации')
  else if (filters.entity === 'entrepreneurs') parts.push('ИП')
  if (filters.search) parts.push(filters.search)
  if (filters.activities?.length) parts.push(`${filters.activities.length} ОКВЭД`)
  if (filters.locations?.length) parts.push(`${filters.locations.length} региона`)
  if (parts.length === 0) return ''
  return parts.join(' · ')
}

export default function JobCreate() {
  const nav = useNavigate()
  const [filters, setFilters] = useState<CheckoFilterParams>({ ...defaultCheckoFilters })
  const [name, setName] = useState('')
  const [headless, setHeadless] = useState(true)
  const [saveRaw, setSaveRaw] = useState(true)
  const [skipDup, setSkipDup] = useState(true)
  const [maxCompanies, setMaxCompanies] = useState('')
  const [minDelay, setMinDelay] = useState('1000')
  const [maxDelay, setMaxDelay] = useState('3000')
  const [concurrency, setConcurrency] = useState(2)
  const [fastExport, setFastExport] = useState(false)

  const sourceUrl = useMemo(() => buildCheckoUrl(filters), [filters])

  const autoName = useMemo(() => {
    const generated = generateJobName(filters)
    return generated || 'Новый сбор'
  }, [filters])

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.search ||
      filters.entity ||
      filters.activities?.length ||
      filters.locations?.length ||
      filters.date_from ||
      filters.date_to ||
      filters.smb ||
      filters.employee ||
      filters.capital ||
      filters.fs_2110 ||
      filters.fs_2400 ||
      filters.fs_1300 ||
      filters.tax_treatment ||
      filters.tax_amount ||
      filters.cn ||
      filters.phones ||
      filters.emails ||
      filters.websites ||
      filters.licenses ||
      filters.trademarks ||
      filters.leasing
    )
  }, [filters])

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: JobCreateRequest = {
        source_url: sourceUrl,
        name: name || autoName || undefined,
        config: {
          headless,
          save_raw_html: saveRaw,
          skip_duplicates: skipDup,
          max_companies: maxCompanies ? Number(maxCompanies) : undefined,
          min_delay_ms: Number(minDelay),
          max_delay_ms: Number(maxDelay),
          concurrency,
          fast_export: fastExport,
        },
      }
      const job = await jobsApi.create(payload)
      // Auto-start after creation
      await jobsApi.start(job.id)
      return job
    },
    onSuccess: (data) => nav(`/jobs/${data.id}`),
  })

  return (
    <div className="fade-in" style={{ maxWidth: 740 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <button onClick={() => nav('/')} className="btn btn-ghost"><ArrowLeft size={18} /></button>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700 }}>Новый сбор</h2>
          <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>Настройте фильтры и запустите задачу сбора данных</p>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Settings2 size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Параметры поиска</div>
        </div>

        {/* Filter controls */}
        <JobFilters filters={filters} onFiltersChange={setFilters} />

        {/* URL Preview */}
        {hasActiveFilters && (
          <div style={{
            marginTop: 8,
            marginBottom: 20,
            padding: '8px 12px',
            background: 'var(--bg-input)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xs)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}>
            <Link size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', wordBreak: 'break-all', lineHeight: 1.5, fontFamily: 'monospace' }}>
              {sourceUrl}
            </div>
          </div>
        )}

        {/* Divider */}
        <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '20px 0' }} />

        {/* Job settings */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>Настройки задачи</div>
        </div>

        <div className="form-group">
          <label className="form-label">Название задачи</label>
          <input
            className="input"
            placeholder={autoName}
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Мин. задержка (мс)</label>
            <input className="input" type="number" value={minDelay} onChange={e => setMinDelay(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Макс. задержка (мс)</label>
            <input className="input" type="number" value={maxDelay} onChange={e => setMaxDelay(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Макс. компаний (пусто = все)</label>
          <input className="input" type="number" placeholder="Без ограничений" value={maxCompanies} onChange={e => setMaxCompanies(e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">Параллельность: {concurrency}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>1</span>
            <input
              type="range"
              min={1}
              max={5}
              step={1}
              value={concurrency}
              onChange={e => setConcurrency(Number(e.target.value))}
              style={{ flex: 1, accentColor: 'var(--accent)' }}
            />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>5</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            {concurrency === 1 ? 'Последовательно (безопасно)' : `${concurrency} вкладок — быстрее, но выше риск капчи`}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          <label className="checkbox-label">
            <input type="checkbox" checked={headless} onChange={e => setHeadless(e.target.checked)} /> Безголовый режим (headless)
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={saveRaw} onChange={e => setSaveRaw(e.target.checked)} /> Сохранять исходный HTML
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={skipDup} onChange={e => setSkipDup(e.target.checked)} /> Пропускать дубликаты
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={fastExport} onChange={e => setFastExport(e.target.checked)} /> Быстрый экспорт (без детальных страниц)
          </label>
        </div>

        {mutation.isError && (
          <div style={{ padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)', color: 'var(--danger)', marginBottom: 16, fontSize: 14 }}>
            {getApiErrorMessage(mutation.error)}
          </div>
        )}

        <button
          className="btn btn-primary btn-lg"
          style={{ width: '100%' }}
          onClick={() => mutation.mutate()}
          disabled={!hasActiveFilters || mutation.isPending}
        >
          {mutation.isPending ? 'Создание...' : 'Создать и запустить'}
        </button>

        {!hasActiveFilters && (
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            Выберите хотя бы один фильтр для начала
          </div>
        )}
      </div>
    </div>
  )
}
